/**
 * 환경 타입
 */
export type AICXEnvironment = 'development' | 'stage' | 'production';

/**
 * SDK가 열 AICX 제품
 *
 * - chatbot: 기존 챗봇
 * - aicc: AI Contact Center(AICC, AI 컨택센터)
 */
export type AICXProduct = 'chatbot' | 'aicc';

/**
 * AICC 위젯이 제공할 화면 범위
 *
 * - support-center: 홈, 상담 내역, 설정을 포함한 전체 고객센터
 * - conversation: 현재 문의 화면만 제공
 */
export type AICXSurface = 'support-center' | 'conversation';

/** 문의 가이드 선택지의 시각 스타일 */
export type InquiryGuideChoiceStyle = 'soft' | 'brand-outline';

/** 문의 가이드 선택지의 배치 방식 */
export type InquiryGuideChoiceLayout = 'wrap' | 'stack';

/**
 * 환경별 chat-app URL 프리셋
 */
export const CHAT_APP_URLS: Record<AICXEnvironment, string> = {
  development: 'https://chat.dev-aicx.kr',
  stage: 'https://chat.aicx.kr',
  production: 'https://chat.aicx.kr',
} as const;

/**
 * AICC 위젯에 표시할 고객사 공개 정보입니다.
 * 인증, tenant 판정, API 주소 선택에는 사용되지 않습니다.
 */
export interface SaaSWidgetDisplayConfig {
  brand?: {
    name?: string;
    logoUrl?: string;
    primaryColor?: string;
    primaryTextColor?: '#000000' | '#FFFFFF';
  };
  home?: {
    greeting?: string;
    announcement?: {
      title?: string;
      content?: string;
    };
  };
  links?: {
    notice?: string;
    faq?: string;
    privacy?: string;
    terms?: string;
  };
  conversation?: {
    /** conversation 화면에서 기존 대화가 생긴 뒤 헤더의 새 문의 동작을 표시할지 여부 */
    showNewInquiry?: boolean;
    inquiryGuide?: {
      /** @default 'soft' */
      choiceStyle?: InquiryGuideChoiceStyle;
      /** @default 'wrap' */
      choiceLayout?: InquiryGuideChoiceLayout;
    };
  };
}

export interface AICXDeliveryAddressEditRequest {
  orderId: string;
  bundleId: string;
  orderOptionId?: string;
}

export interface AICXDeliveryAddressEditData {
  recipient: string;
  receivedPhoneNumber: string;
  zipCode: string;
  address: string;
  addressDetail: string;
  deliveryMemo: string;
}

/** 새 문의 시작 시 서버의 필수 조회 인자로 사용할 구조화된 문자열 문맥입니다. */
export type AICXClientContext = Record<string, string>;

/**
 * SDK 초기화 설정
 *
 * @example
 * ```ts
 * // 환경으로 자동 설정
 * AICX.init({
 *   serviceKey: 'your-service-key',
 *   environment: 'production',
 * });
 *
 * // 또는 URL 직접 지정 (로컬 개발 등)
 * AICX.init({
 *   serviceKey: 'your-service-key',
 *   chatAppUrl: 'http://localhost:5173',
 * });
 * ```
 */

export interface AICXConfig {
  /**
   * 서비스 키 (필수)
   * Admin에서 발급되는 tenant_public_id 기반 고유 식별자
   * iframe URL 경로에 포함됩니다: chat.aicx.kr/{serviceKey}
   *
   * @example 'abc123-def456'
   */
  serviceKey: string;

  /**
   * SDK가 열 AICX 제품
   * 생략하거나 'chatbot'이면 기존 chat-app URL과 동작을 그대로 사용합니다.
   * 'aicc'이면 같은 chat-web 호스트의 /widget/{serviceKey}를 사용합니다.
   *
   * @default 'chatbot'
   */
  product?: AICXProduct;

  /**
   * AICC 위젯의 화면 범위입니다.
   * product가 'aicc'일 때만 사용할 수 있으며, 생략하면 기존 전체 고객센터를 엽니다.
   *
   * @default 'support-center'
   */
  surface?: AICXSurface;

  /**
   * AICX API 인증 세션입니다.
   * SDK와 iframe의 메모리에서만 유지하며 URL이나 브라우저 저장소에 저장하지 않습니다.
   * guest 세션은 생략할 수 있습니다.
   */
  aicxSessionToken?: string;

  /**
   * 오늘의집 배송지 편집 화면에서 사용자가 명시적으로 요청한 경우에만 원본 배송지를 조회합니다.
   * 반환값은 iframe 메모리로 한 번 전달되며 SDK 저장소나 채팅 이력에는 저장하지 않습니다.
   */
  deliveryAddressProvider?: (
    request: AICXDeliveryAddressEditRequest
  ) => Promise<AICXDeliveryAddressEditData | null>;

  /**
   * 환경 설정
   * - 'development': chat.dev-aicx.kr (개발/테스트)
   * - 'stage': chat.aicx.kr (스테이징/운영 도메인)
   * - 'production': chat.aicx.kr (운영)
   *
   * chatAppUrl이 지정되면 이 옵션은 무시됩니다.
   */
  environment?: AICXEnvironment;

  /**
   * iframe에 로드할 chat-app URL (직접 지정)
   * 지정하면 environment 설정보다 우선합니다.
   * 로컬 개발이나 커스텀 환경에서 사용합니다.
   *
   * @example 'http://localhost:5173'
   */
  chatAppUrl?: string;

  /**
   * 챗봇을 붙일 DOM 요소 ID
   * 지정하지 않으면 document.body에 추가됨
   * @default undefined (body에 추가)
   */
  containerId?: string;

  /**
   * 테마 설정
   * @default 'light'
   */
  theme?: 'light' | 'dark';

  /**
   * 디버그 모드 활성화
   * true면 콘솔에 로그 출력
   * @default false
   */
  debug?: boolean;

  /**
   * 챗봇 위치
   * @default 'bottom-right'
   */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

  /**
   * 챗봇 너비 (px)
   * @default 400
   */
  width?: number;

  /**
   * 챗봇 높이 (px)
   * @default 600
   */
  height?: number;

  /**
   * z-index 값
   * @default 999999
   */
  zIndex?: number;

  /**
   * 챗봇 표시 모드
   * - full: 전체 화면 (기본값)
   * - floating: 우하단 플로팅 버튼 형태
   * @default 'full'
   */
  displayMode?: 'full' | 'floating';

  /**
   * 사용자 식별자
   * 지정하지 않으면 chat-app에서 자동 생성 (crypto.randomUUID)
   *
   * @example 'user-12345'
   */
  userId?: string;

  /** 분석 이벤트에서 로그인 사용자를 연결할 비식별 키 */
  userKey?: string;

  /** 고객센터 진입부터 채팅까지 같은 여정으로 연결할 UUID */
  journeyId?: string;

  /**
   * 채팅 UI에 표시할 챗봇 이름
   * 미지정 시 chat-app 기본값 사용
   */
  botName?: string;

  /**
   * 채팅 UI에 표시할 챗봇 프로필 이미지 URL
   * 미지정 시 chat-app 기본 아이콘/이미지 사용
   */
  botProfileImage?: string;

  /**
   * 특정 채팅방 ID를 직접 지정합니다.
   * 지정하면 localStorage에 저장된 chatId보다 우선해 해당 채팅으로 진입합니다.
   *
   * @example '20a27528-4562-4f0c-ada2-aa9a7fd05c40'
   */
  chatId?: string;

  /**
   * 외부 채팅 URL에서 chatId를 추출해 진입합니다.
   * chatId가 함께 지정된 경우 chatId가 우선합니다.
   *
   * @example 'https://chat.dev-aicx.kr/{serviceKey}/chat/{chatId}'
   */
  chatUrl?: string;

  /**
   * 사전 컨텍스트 (첫 메시지에 포함)
   * 예약 정보, 고객 정보 등 상담에 필요한 사전 정보를 전달합니다.
   *
   * @example '예약번호: RES123\n고객명: 홍길동\n연락처: 010-1234-5678'
   */
  preContext?: string;

  /**
   * 새 채팅의 첫 요청에서만 전달되는 구조화된 조회 문맥입니다.
   * 브라우저 메모리에만 보관되며 인증 또는 권한 근거로 사용되지 않습니다.
   *
   * @example { reservationCode: 'OT-A-1001' }
   */
  clientContext?: AICXClientContext;

  /**
   * 첫 사용자 메시지 처리 전략
   * - 'use_input': sendMessage로 전달된 값을 그대로 첫 메시지로 사용
   * - 'use_configured': firstMessage가 있으면 첫 메시지를 해당 값으로 강제
   * @default 'use_input'
   */
  firstMessageStrategy?: 'use_input' | 'use_configured';

  /**
   * 첫 사용자 메시지 고정값
   * firstMessageStrategy가 'use_configured'일 때만 사용됩니다.
   *
   * @example '안녕하세요, 예약 변경 문의드려요.'
   */
  firstMessage?: string;

  /**
   * 채팅방 입장 시 chat-app이 먼저 노출할 봇 환영 메시지
   * - 화면에 봇 말풍선으로만 노출되며, 사용자 전송 메시지로 처리되지 않습니다.
   * - 주로 "첫 안내 멘트" 표시 용도로 사용합니다.
   */
  initialBotMessage?: string;

  /**
   * 채팅 배경색 커스텀 값
   * - CSS color 문자열(HEX/RGB/RGBA/HSL 등)
   * - 미지정 시 chat-app 기본 토큰 색상을 사용합니다.
   */
  chatBackgroundColor?: string;

  /**
   * 봇 말풍선 배경색 커스텀 값
   * - CSS color 문자열(HEX/RGB/RGBA/HSL 등)
   * - 미지정 시 기본 토큰(gray-80)을 사용합니다.
   */
  botBubbleBackgroundColor?: string;

  /**
   * 사용자 말풍선 배경색 커스텀 값
   * - CSS color 문자열(HEX/RGB/RGBA/HSL 등)
   * - 미지정 시 기본 토큰(primary)을 사용합니다.
   */
  userBubbleBackgroundColor?: string;

  /**
   * AICC 위젯 전용 고객사 표시 설정
   * 미지정 시 기존 앱과 AICC 위젯의 기본 표시를 유지합니다.
   */
  saasWidgetDisplayConfig?: SaaSWidgetDisplayConfig;
}
